import pick from 'lodash/pick';

import {isClauseRoot} from 'common/query-builder/cohorts-query-builder/util/expression-tree';

import {OperatorNode} from '../expression/operator-node';
import type {CohortId} from '../types/expression-types';
import type {SerializedCohortLiteral} from '../types/serialized-staged-cohort-types';
import {CohortClause} from './cohort-clause';
import {Literal} from './literal';

interface CohortLiteralOptions {
  clauses: CohortClause[];
  labelPrefix: string;
}

export class CohortLiteral extends Literal {
  public static deserialize(options: SerializedCohortLiteral): CohortLiteral {
    return new CohortLiteral({
      clauses: options.clauses.map((c) => CohortClause.deserialize(c)),
      labelPrefix: options.labelPrefix,
    });
  }

  public static createDefaultWithPrefix(labelPrefix: string): CohortLiteral {
    return new CohortLiteral({
      clauses: [CohortClause.createDefault()],
      labelPrefix,
    });
  }

  public static createFromParts({tree, labelPrefix}: {tree: OperatorNode; labelPrefix: string}): CohortLiteral {
    if (!isClauseRoot(tree)) {
      throw new Error(`Invalid tree for CohortLiteral`);
    }

    // NOTE: This code assumes each literal will only have one cohort clause.
    const clauses = [CohortClause.createFromParts(tree.left)];
    return new CohortLiteral({
      clauses,
      labelPrefix,
    });
  }

  public readonly clauses: CohortClause[];

  constructor(options: CohortLiteralOptions) {
    super({
      ...pick(options, [`labelPrefix`]),
      clauses: options.clauses,
    });
  }

  public serialize(): SerializedCohortLiteral {
    return {
      clauses: this.clauses.map((c) => c.serialize()),
      labelPrefix: this.labelPrefix,
    };
  }

  public addDefaultClause(): CohortLiteral {
    throw new Error(`addDefaultClause not supported for CohortLiteral`);
  }

  public setCohortId({
    cohortId,
    clauseIdx,
    isDependentCohort,
  }: {
    cohortId: CohortId;
    clauseIdx: number;
    isDependentCohort: boolean;
  }): CohortLiteral {
    return this.replaceClause<CohortLiteral>(
      this.clauses[clauseIdx].setCohortId({cohortId, isDependentCohort}),
      clauseIdx,
    );
  }

  public setClauseNegated(negated: boolean, clauseIdx: number): CohortLiteral {
    return this.replaceClause<CohortLiteral>(this.clauses[clauseIdx].setNegated(negated), clauseIdx);
  }

  public addPropertyFilter(clauseIdx: number): CohortLiteral {
    return this.replaceClause<CohortLiteral>(this.clauses[clauseIdx].addPropertyFilter(), clauseIdx);
  }

  public setClauseOperators(): CohortLiteral {
    throw new Error(`setClauseOperators not supported for CohortLiteral`);
  }

  public toTrackingData(): Obj {
    const hasClauseOperators = !!this.clauseOperators;
    return {
      'initial operator': hasClauseOperators ? this.clauseOperators[0] : null,
      'is cohort literal': true,
      'join operator': hasClauseOperators ? this.clauseOperators[1] : null,
      'literal clauses': this.clauses.length,
    };
  }

  public sanitizeForDataGroup({dataGroupId, cohorts}): Literal {
    let newClauses = this.clauses.map((clause) => clause.removeInvalidPropertyFilters(dataGroupId));
    newClauses = newClauses.filter((clause) => !clause.isInvalidForDataGroup({dataGroupId, cohorts}));
    return this.newLiteral({clauses: newClauses});
  }

  protected getOperatorLabelAtIndex(idx: number): string {
    const operatorIdx = Math.min(idx - 1, 1);
    return this.clauseOperators[operatorIdx];
  }

  // @override
  protected newLiteral(options: Partial<CohortLiteralOptions>): CohortLiteral {
    const oldOptions: CohortLiteralOptions = pick(this, [`clauses`, `labelPrefix`]);
    return new CohortLiteral({...oldOptions, ...options});
  }
}
